import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone,
  BarChart3,
  Briefcase,
  Users,
  CheckCircle2
} from 'lucide-react';

const personas = [
  {
    id: 'everyone',
    icon: Users,
    title: 'Для всех',
    headline: 'Что получает любая команда',
    color: 'bg-[#00bfa5]',
    borderColor: 'border-[#00bfa5]',
    shadowColor: 'shadow-[8px_8px_0_0_#00bfa5]',
    description: 'Базовые возможности для каждой роли. Безопасность, надёжность и гибкость — с первого дня.',
    benefits: [
      'Соответствие SOC 2 и безопасность уровня enterprise',
      'Синхронизация данных из всех источников в реальном времени',
      'SLA доступности 99,9%',
      'Полная выгрузка — данные всегда остаются вашими'
    ],
    quote: '«Просто работает. Каждый раз и для всей команды.»',
    role: '— Операционный директор'
  },
  {
    id: 'marketers',
    icon: Megaphone,
    title: 'Для маркетологов',
    headline: 'Наконец видно, что работает',
    color: 'bg-[#ff6b35]',
    borderColor: 'border-[#ff6b35]',
    shadowColor: 'shadow-[8px_8px_0_0_#ff6b35]',
    description: 'Подключите все рекламные каналы и смотрите на единую картину эффективности. Без хаоса в таблицах.',
    benefits: [
      'Все кампании со всех площадок в одном отчёте',
      'Автоматический расчёт ROAS и атрибуции',
      'ИИ-рекомендации по распределению бюджета',
      'Мгновенные алерты при просадке метрик'
    ],
    quote: '«Раньше отчёт собирался два дня. Теперь я просто спрашиваю ИИ.»',
    role: '— Директор по маркетингу'
  },
  {
    id: 'analysts',
    icon: BarChart3,
    title: 'Для аналитиков',
    headline: 'Занимайтесь анализом, а не пайплайнами',
    color: 'bg-[#4361ee]',
    borderColor: 'border-[#4361ee]',
    shadowColor: 'shadow-[8px_8px_0_0_#4361ee]',
    description: 'Никакой возни с инфраструктурой. Данные очищены, смоделированы и готовы к анализу с первого дня.',
    benefits: [
      'Готовые модели данных под типовые задачи',
      'SQL-доступ ко всем подключённым данным',
      'Трансформации под контролем версий',
      'Мгновенная выгрузка в любой BI'
    ],
    quote: '«Наконец-то я занимаюсь анализом, а не чиню сломанные пайплайны.»',
    role: '— Ведущий аналитик данных'
  },
  {
    id: 'owners',
    icon: Briefcase,
    title: 'Для бизнеса',
    headline: 'Знайте свои цифры без отдела аналитики',
    color: 'bg-[#d4ff00]',
    borderColor: 'border-[#d4ff00]',
    shadowColor: 'shadow-[8px_8px_0_0_#d4ff00]',
    description: 'Аналитика как у больших компаний — без единого дата-инженера в штате.',
    benefits: [
      'Дашборды для руководителя из коробки',
      'Вопросы обычными словами — без SQL',
      'Дешевле, чем строить всё самим',
      'Данные остаются вашими при любом росте'
    ],
    quote: '«Мы получили аналитику уровня enterprise без бюджета enterprise.»',
    role: '— CEO стартапа на раунде A'
  }
];

export default function UserPersonas() {
  const [activePersona, setActivePersona] = useState('everyone');
  const active = personas.find(p => p.id === activePersona);

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 right-20 w-40 h-40 border-4 border-black/10 rotate-12" />
      <div className="absolute bottom-20 left-20 w-32 h-32 border-4 border-black/10 rounded-full" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-block bg-black text-[#d4ff00] px-4 py-2 font-bold text-sm uppercase tracking-wider border-4 border-black mb-6">
            Сделано под вас
          </div>
          <h2 className="text-[clamp(1.5rem,8.6vw,2.25rem)] md:text-6xl font-black text-black mb-4">
            Ваша роль.
            <br />
            <span className="text-black/40">Ваши суперсилы.</span>
          </h2>
        </motion.div>

        {/* Persona tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {personas.map((persona) => (
            <button
              key={persona.id}
              onClick={() => setActivePersona(persona.id)}
              className={`flex items-center gap-3 px-6 py-4 font-bold text-lg border-4 border-black transition-all cursor-pointer ${
                activePersona === persona.id
                  ? `${persona.color} shadow-[4px_4px_0_0_#1a1a1a]`
                  : 'bg-white hover:shadow-[4px_4px_0_0_#1a1a1a] hover:translate-x-[-2px] hover:translate-y-[-2px]'
              }`}
            >
              <persona.icon className="w-5 h-5" />
              {persona.title}
            </button>
          ))}
        </div>

        {/* Active persona content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activePersona}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`bg-[#f5f5f0] border-4 border-black p-8 md:p-12 ${active.shadowColor}`}
          >
            <div className="grid md:grid-cols-2 gap-12">
              {/* Left side - Main content */}
              <div>
                <div className={`w-16 h-16 ${active.color} border-4 border-black flex items-center justify-center mb-6`}>
                  <active.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-[clamp(1.375rem,7.4vw,1.875rem)] md:text-4xl font-black text-black mb-4">
                  {active.headline}
                </h3>
                <p className="text-lg text-black/70 font-medium mb-8">
                  {active.description}
                </p>

                {/* Quote */}
                <div className={`border-l-4 ${active.borderColor} pl-6 py-2`}>
                  <p className="text-xl font-bold text-black italic mb-2">
                    {active.quote}
                  </p>
                  <p className="text-black/60 font-medium">{active.role}</p>
                </div>
              </div>

              {/* Right side - Benefits */}
              <div className="bg-white border-4 border-black p-6 md:p-8">
                <h4 className="font-black text-lg text-black mb-6 uppercase tracking-wider">
                  Что вы получаете
                </h4>
                <ul className="space-y-4">
                  {active.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`w-6 h-6 ${active.color} border-2 border-black flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <CheckCircle2 className="w-4 h-4 text-black" />
                      </div>
                      <span className="font-medium text-black">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
